// NewsTalk (c) Copyright 2023 by Qwixotic
// 
// File:   AccountSettings.js
// Author: Percipient Reverence
// Date:   15-Aug-2023
//
// Description:
//
//   For members to change their account settings.

// React imports
import React,   { useMemo, useState, useEffect} from 'react'
import          { useOutletContext, useParams } from 'react-router-dom'
import Dropzone                                 from 'react-dropzone'

// MUI imports
import AddIcon        from '@mui/icons-material/Add';
import Box            from '@mui/material/Box'
import Button         from '@mui/material/Button'
import Grid           from '@mui/material/Grid'
import IconButton     from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Paper          from '@mui/material/Paper'
import Popover        from '@mui/material/Popover'
import Radio          from '@mui/material/Radio'
import RadioGroup     from '@mui/material/RadioGroup'
import RemoveIcon     from '@mui/icons-material/Remove';
import Stack          from '@mui/material/Stack'
import { styled }     from '@mui/material/styles';
import Table          from '@mui/material/Table';
import TableBody      from '@mui/material/TableBody';
import TableRow       from '@mui/material/TableRow';
import { TableCell as MuiTableCell } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import TextField      from '@mui/material/TextField';
import Tooltip        from '@mui/material/Tooltip'
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff  from '@mui/icons-material/VisibilityOff';

// Local imports
import { MainPageButton, MessageBox, AvatarDisplay }    from './lib/Components'
import { emailIsLegal, myFetch, myPost, myPostForm, nameIsLegal, passwordIsLegal, setSigninCookies } from './lib/Util'

const AliasBox = (props) => {
  const [aliasError, setAliasError] = useState(false)
  const [aliasHelperText, setAliasHelperText] = useState(null)
  const [submittedAlias, setSubmittedAlias] = useState('')

  const onChangeCheckAliasField = (target) => {
    if (aliasError) {
      if (nameIsLegal(target.value)) {
        setAliasError(false)
        setAliasHelperText(null)
      }
    }
  }

  const submitAlias = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const alias = Data.get('alias')

    if (nameIsLegal(alias)) {
      // Legal alias
      setAliasError(false)
      setSubmittedAlias(alias)
    } else {
      // Illegal alias, indicate error
      setAliasError(true)
      setAliasHelperText('Aliases may contain letters, numbers and the characters "!?/$&^+-~.:;\'"[]{}" ' +
                         'and cannot be longer than 20 characters ')
      return
    }
  }

  const {signinCredentials, credentialSetter, aliasPopoverAnchorElSetter} = props
  const mbrID = signinCredentials.idnum

  useEffect(() => {
    if (submittedAlias) {
      const alias = submittedAlias
      setSubmittedAlias(null)
      const body = {
        MBRID: mbrID,
        alias: alias
      }
      myPost('/nt/addAlias', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(aliasResult => {
          if (aliasResult.status === 'Success') {
            credentialSetter(aliasResult.data)
            aliasPopoverAnchorElSetter(null)
          } else {
            setAliasError(true)
            setAliasHelperText (aliasResult.message)
          }
        })
        .catch((err) => {
          console.log('*** ERROR *** Failure while fetching member aliases')
        })
    }
  }, [submittedAlias, mbrID, credentialSetter, aliasPopoverAnchorElSetter])

  return (
    <Stack direction='row' justifyContent='space-evenly' alignItems='center' component='form' onSubmit={submitAlias}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '25rem'}}>
      <Tooltip title='Enter the new alias' placement='top'>
        <TextField id='alias' name='alias' label='New Alias' variant='outlined' fullwidth='true' autoFocus={true}
                   error={aliasError}
                   helperText={aliasHelperText}
                   onChange={(event) => onChangeCheckAliasField(event.currentTarget, aliasError)}
                   sx={{margin: '.5rem', width: '15rem',
                        '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                     '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      </Tooltip>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Add
        </Button>
      </Box>
    </Stack>
  )
}

const AliasList = (props) => {
  const [aliasPopoverAnchorEl, setAliasPopoverAnchorEl] = useState(null)
  const [aliasDeletion, setAliasDeletion] = useState('')
  const [newPrimaryAlias, setNewPrimaryAlias] = useState('')
  const displayAliasPopover = (event) => {
    setAliasPopoverAnchorEl(event.currentTarget)
  }
  const closeAliasPopover = () => {
    setAliasPopoverAnchorEl(null)
  }
  const open = useMemo(() => {
    return Boolean(aliasPopoverAnchorEl)
  }, [aliasPopoverAnchorEl])
  const id = open ? 'aliasPopoverID' : undefined
  const Items = []
  let primaryAlias = ''
  let count = 0

  const {signinCredentials, credentialSetter} = props
  const mbrID = signinCredentials.idnum

  useEffect(() => {
    if (aliasDeletion) {
      const body = {
        MBRID: mbrID,
        alias: aliasDeletion
      }
      myPost('/nt/deleteAlias', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(deleteAliasResult => {
          if (deleteAliasResult.status === 'Success') {
            credentialSetter(deleteAliasResult.data)
          } else {
            throw Error(deleteAliasResult.message)
          }
        })
        .catch(err => {
          throw Error('*** Error *** Failure while attempting to delete alias')
        })
    }
  }, [aliasDeletion, mbrID, credentialSetter])

  useEffect(() => {
    if (newPrimaryAlias) {
      const body = {
        MBRID: mbrID,
        alias: newPrimaryAlias
      }
      setNewPrimaryAlias('')
      myPost('/nt/changePrimaryAlias', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(changePrimaryAliasResult => {
          if (changePrimaryAliasResult.status === 'Success') {
            credentialSetter(changePrimaryAliasResult.data)
          } else {
            throw Error(changePrimaryAliasResult.message)
          }
        })
        .catch(err => {
          throw Error('*** Error *** Failure while attempting to change primary alias')
        })
    }
  }, [newPrimaryAlias, mbrID, credentialSetter])

  let first = true
  const Aliases = props.signinCredentials.Aliases
  for (const Alias of Aliases) {
    if (Alias.Prim) {
      primaryAlias = Alias.Name
    }
    Items.push(
      <Grid key={'al0_' + count++} container justifyContent='flex-start' spacing={2}>
        <Grid item justifyContent='flex-start' alignItems='flex-end' xs={5}>
          <Radio size='medium' value={Alias.Name} onClick={() => setNewPrimaryAlias(Alias.Name)}/>
          {Alias.Name}
        </Grid>
        <Grid item justifyContent='flex-start' xs={2}>
          {Aliases.length === 1
           ? null
           : <Tooltip title='Remove this alias' placement='top'>
               <IconButton onClick={(event) => setAliasDeletion(Alias.Name)}>
                 <RemoveIcon/>
               </IconButton>
             </Tooltip>
          }
        </Grid>
        <Grid item justifyContent='flex-start' xs={2}>
          {!first
           ? null
           : <Tooltip title='Define a new alias' placement='top'>
               <IconButton onClick={(event) => displayAliasPopover(event)}>
                 <AddIcon/>
               </IconButton>
             </Tooltip>
          }
        </Grid>
      </Grid>)
    first = false
  }
  return (
    <>
      <RadioGroup name='Aliases' value={primaryAlias}>
        {Items}
      </RadioGroup>
      <Popover id={id} open={Boolean(aliasPopoverAnchorEl)}
               sx={{'& .css-3bmhjh-MuiPaper-root-MuiPopover-paper': {borderRadius: '8px'}}}
               anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
               transformOrigin={{vertical: 'top', horizontal: 'center'}}
               anchorEl={aliasPopoverAnchorEl} onClose={closeAliasPopover}>
        <AliasBox credentialSetter={props.credentialSetter} signinCredentials={props.signinCredentials}
                  aliasPopoverAnchorElSetter={setAliasPopoverAnchorEl}/>
      </Popover>
    </>
  )
}
               
const EmailBox = (props) => {
  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailHelperText, setEmailHelperText] = useState(null)

  const onChangeCheckEmailField = (target) => {
    if (emailError) {
      if (emailIsLegal(target.value)) {
        setEmailError(false)
        setEmailHelperText(null)
      }
    }
  }

  const submitEmail = (event) => {
    event.preventDefault()
    const Data = new FormData(event.currentTarget)
    const theEmail = Data.get('email')

    if (emailIsLegal(theEmail)) {
      // Legal email
      setNewEmail(theEmail)
      setEmailError(false)
    } else {
      // Illegal email, indicate error
      setEmailError(true)
      setEmailHelperText('Please enter a legal email address')
      return
    }
  }

  const {signinCredentials, stateSetter} = props
  const mbrID = signinCredentials.idnum

  useEffect(() => {
    if (newEmail) {
      const body = {
        MBRID: mbrID,
        email: newEmail
      }
      myPost('/nt/changeEmail', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(newEmailResult => {
          if (newEmailResult.status === 'Success') {
            // Email change requests cause a validation email to be sent to the member, but nothing
            // changes for now, so just close the popover.
            stateSetter('acknowledge')
          } else {
            throw Error(newEmailResult.message)
          }
        })
        .catch(err => {
          throw Error(`*** Error *** Failure while attempting to change email: ${err}`)
        })
    }
  }, [newEmail, stateSetter, mbrID])

  return (
    <Stack direction='row' justifyContent='space-evenly' alignItems='center' component='form' onSubmit={submitEmail}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', width: '25rem'}}>
      <Tooltip title='Enter the new email address' placement='top'>
        <TextField id='email' name='email' label='New Email Address' variant='outlined' fullwidth='true' autoFocus={true}
                   error={emailError}
                   helperText={emailHelperText}
                   onChange={(event) => onChangeCheckEmailField(event.currentTarget, emailError)}
                   sx={{margin: '.5rem', width: '15rem',
                        '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                     '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      </Tooltip>
      <Box sx={{textAlign: 'center'}}>
        <Button variant='contained' type='submit'>
          Change
        </Button>
      </Box>
    </Stack>
  )
}

const EmailAcknowledgeBox = (props) => {
  return (
    <Stack direction='column' alignItems='center' spacing={3}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', fontSize: '14pt', width: '25rem'}}>
      <Box justifyContent='center' fullwidth='true'>
          Validation email sent.
      </Box>
      <Box>
        <Button variant='contained' onClick={() => {
                  props.popoverAnchorElSetter(null)
                  setTimeout(() => props.stateSetter('emailbox'), 200)
                }}>
          Close
        </Button>
      </Box>
    </Stack>
  )
}

const EmailSetting = (props) => {
  const [emailPopoverAnchorEl, setEmailPopoverAnchorEl] = useState(null)
  const displayEmailPopover = (event) => {
    setEmailPopoverAnchorEl(event.currentTarget)
  }
  const closeEmailPopover = () => {
    setEmailPopoverAnchorEl(null)
  }
  const open = useMemo(() => {
    return Boolean(emailPopoverAnchorEl)
  }, [emailPopoverAnchorEl])
  const id = open ? 'emailPopoverID' : undefined

  const {signinCredentials} = props

  const [popoverState, setPopoverState] = useState('emailbox')
  const popoverBox = useMemo(() => {
    switch (popoverState) {
    default:
    case 'emailbox':
      return <EmailBox signinCredentials={signinCredentials} stateSetter={setPopoverState}/>
    case 'acknowledge':
      return <EmailAcknowledgeBox stateSetter={setPopoverState} popoverAnchorElSetter={setEmailPopoverAnchorEl}/>
    }
  }, [popoverState, signinCredentials])

  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      <TextField id='email' name='email' label='Email' variant='outlined' fullwidth='true' readOnly={true}
                 value={props.signinCredentials.email}
                 sx={{margin: '.5rem', width: '15rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <Button variant='contained' onClick={(event) => displayEmailPopover(event)} sx={{width: '15rem'}}>
        Change Email Address
      </Button>
      <Popover id={id} open={Boolean(emailPopoverAnchorEl)}
               sx={{'& .css-3bmhjh-MuiPaper-root-MuiPopover-paper': {borderRadius: '8px'}}}
               anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
               transformOrigin={{vertical: 'top', horizontal: 'right'}}
               anchorEl={emailPopoverAnchorEl} onClose={closeEmailPopover}>
        {popoverBox}
      </Popover>
    </Stack>
  )
}

export const EmailValidationEP = () => {
  const {signinCredentials, credentialSetter} = useOutletContext()
  const Params = useParams()
  const mbrID = Params.MBRID
  const emailValidationCode = Params.Code ? encodeURIComponent(Params.Code) : ''
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (mbrID && emailValidationCode && !result) { // Only do this once
      console.log(`Calling /nt/emailValidation: ${mbrID}, ${emailValidationCode}, ${result}`)
      myFetch(`/nt/emailValidation/${mbrID}/${emailValidationCode}`)
        .then(res => {
          if (!res.ok) {
            throw Error(res.statusText)
          }
          return res.json()
        })
        .then(emailValidationResult => {
          if (emailValidationResult.status !== 'Success') {
            throw Error(emailValidationResult.message)
          }
          credentialSetter(emailValidationResult.data)
          setSigninCookies(emailValidationResult.data.idnum, emailValidationResult.data.pword)
          setResult(emailValidationResult)
        })
        .catch((err) => {
          console.log('*** ERROR *** Failure while validating new email address: ' + err)
        })
    }
  }, [credentialSetter, emailValidationCode, mbrID, result])

  return (
    !result
      ? 'Working...'
      : (result.status === 'Error'
         ? <MessageBox color='red'>
             Unable to validate new email address.  Your current email address is unchanged.
           </MessageBox>
         : <MessageBox>
             <Stack direction='column' justifyContent='center'>
               <p style={{textAlign: 'center'}}>
                 New email address for member {signinCredentials.pname} has been successfully validated:
               </p>
               <p style={{textAlign: 'center'}}>
                 New Email Address: {signinCredentials.email}
               </p>
               <p style={{textAlign: 'center'}}>
                 {signinCredentials.pname} is signed in.
               </p>
               <MainPageButton/>
             </Stack>
           </MessageBox>
        )
  )
}

export const RegValidationEP = () => {
  const {signinCredentials, credentialSetter} = useOutletContext()
  const Params = useParams()
  const mbrID = Params.MBRID
  const regValidationCode = Params.Code ? encodeURIComponent(Params.Code) : ''
  const [result, setResult] = useState(null)

  useEffect(() => {
    if (mbrID && regValidationCode && !result) { // Only do this once
      console.log(`Calling /nt/regValidation: ${mbrID}, ${regValidationCode}, ${result}`)
      myFetch(`/nt/regValidation/${mbrID}/${regValidationCode}`)
        .then(res => {
          if (!res.ok) {
            throw Error(res.statusText)
          }
          return res.json()
        })
        .then(regValidationResult => {
          if (regValidationResult.status !== 'Success') {
            throw Error(regValidationResult.message)
          }
          credentialSetter(regValidationResult.data)
          setSigninCookies(regValidationResult.data.idnum, regValidationResult.data.pword)
          setResult(regValidationResult)
        })
        .catch((err) => {
          setResult({status: 'Error', message: `Registration validation failed because: ${err}`})
          console.log('*** ERROR *** Failure while validating registration: ' + err)
        })
    }
  }, [credentialSetter, regValidationCode, mbrID, result])

  return (
    !result
      ? 'Working...'
      : (result.status === 'Error'
         ? <MessageBox color='red'>
             Unable to validate new member registration.
           </MessageBox>
         : <MessageBox>
             <Stack direction='column' justifyContent='center'>
               <p style={{textAlign: 'center'}}>
                 New member {signinCredentials.pname} has been successfully validated.
               </p>
               <p style={{textAlign: 'center'}}>
                 {signinCredentials.pname} is signed in.
               </p>
               <MainPageButton/>
             </Stack>
           </MessageBox>
        )
  )
}

const PasswordAcknowledgeBox = (props) => {
  return (
    <Stack direction='column' alignItems='center' spacing={3}
           sx={{backgroundColor: '#eee', padding: '.5rem', borderStyle: 'solid', borderRadius: '8px',
                fontFamily: 'Roboto', fontSize: '14pt', width: '25rem'}}>
      <Box justifyContent='center' fullwidth='true'>
        {props.acknowledgeText}
      </Box>
      <Box>
        <Button variant='contained' onClick={() => {
                  props.popoverAnchorElSetter(null)
                }}>
          Close
        </Button>
      </Box>
    </Stack>
  )
}

const PasswordChangeRequest = (props) => {
  const [passwordPopoverAnchorEl, setPasswordPopoverAnchorEl] = useState(null)
  const [acknowledgeText, setAcknowledgeText] = useState('')
  const displayPasswordPopover = (event) => {
    setPasswordPopoverAnchorEl(event.currentTarget)
  }
  const closePasswordPopover = () => {
    setPasswordPopoverAnchorEl(null)
  }
  const open = useMemo(() => {
    return Boolean(passwordPopoverAnchorEl)
  }, [passwordPopoverAnchorEl])
  const id = open ? 'passwordPopoverID' : undefined

  const {signinCredentials} = props
  const mbrID = signinCredentials.idnum

  // Send email with button for initiating password change
  useEffect(() => {
    if (passwordPopoverAnchorEl) {
      if (mbrID) {
        myFetch(`/nt/passwordChangeRequest/${mbrID}`)
          .then(res => {
            if (!res.ok) {
              throw Error(res.statusText)
            }
            return res.json()
          })
          .then(passwordChangeRequestResult => {
            if (passwordChangeRequestResult.status === 'Success') {
              setAcknowledgeText('Password change request email sent.')
            } else {
              setAcknowledgeText('Password change request failed.')
            }
          })
          .catch(err => {
            console.log('*** ERROR *** Failure requesting password change: ' + err)
            setAcknowledgeText('Password change request failed.')
          })
      } else {
        console.log('*** ERROR *** Password change requested while not signed in')
        setAcknowledgeText('Password change request failed.')
      }
    }
  }, [passwordPopoverAnchorEl, mbrID])

  return (
    <Stack direction='row' alignItems='center' spacing={2}>
      <TextField id='password' name='password' label='Password' variant='outlined' fullwidth='true' disabled={true}
                 value='Password Hidden'
                 sx={{margin: '.5rem', width: '15rem',
                      '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                   '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
      <Button variant='contained' onClick={(event) => displayPasswordPopover(event)} sx={{width: '15rem'}}>
        Request Password Change
      </Button>
      <Popover id={id} open={Boolean(passwordPopoverAnchorEl)}
               sx={{'& .css-3bmhjh-MuiPaper-root-MuiPopover-paper': {borderRadius: '8px'}}}
               anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
               transformOrigin={{vertical: 'top', horizontal: 'right'}}
               anchorEl={passwordPopoverAnchorEl} onClose={closePasswordPopover}>
        <PasswordAcknowledgeBox acknowledgeText={acknowledgeText} popoverAnchorElSetter={setPasswordPopoverAnchorEl}/>
      </Popover>
    </Stack>
  )
}

// Brings up a page where member can enter a new password.  We don't check the sign in cookies or
// set sign in credentials.  A member sets his password using this page that isn't signed in.  But
// if the password change is successful then the sign in credentials and cookied are set.
export const PasswordEnterEP = () => {
  const {signinCredentials, credentialSetter} = useOutletContext()
  const Params = useParams()
  const mbrID = Number(Params.MBRID)
  const encodedPasswordValidationCode = Params.Code ? Params.Code : ''
  const [showPassword, setShowPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(false)
  const [passwordHelperText, setPasswordHelperText] = useState(null)
  const handleClickShowPassword = () => setShowPassword((show) => !show)
  const handleMouseDownPassword = (event) => {event.preventDefault()}
  const [checkPassword, setCheckPassword] = useState('')
  const [newPasswordSuccess, setNewPasswordSuccess] = useState(false)

  // ```` Is there a better way to make a server request than useEffect, since that isn't called
  // until after the first render?  There's something called componentWillMount that I should look
  // into.

  const onChangeCheckPasswordField = (target) => {
    if (passwordError) {
      if (passwordIsLegal(target.value)) {
        setPasswordError(false)
        setPasswordHelperText(null)
      }
    }
  }

  const submitPasswordChange = (event) => {
    console.log('sPC a')
    event.preventDefault()
    const Data = new FormData(event.target)
    const newPassword = Data.get('password')
    if (passwordIsLegal(newPassword)) {
    console.log('sPC b')
      setPasswordError(false)
    } else {
    console.log('sPC c')
      setPasswordError(true)
      setPasswordHelperText('Password must be at least 6 characters')
      return
    }
    setCheckPassword(newPassword)
  }

  const [MemberInfo, setMemberInfo] = useState({pname: ''})
  useEffect(() => {
    myFetch(`/nt/fetchMemberInfo/${mbrID}`)
      .then(res => {
        if (!res.ok) {
          throw Error(res.statusText)
        }
        return res.json()
      })
      .then(MemberInfoResult => {
        if (MemberInfoResult.status === 'Error') {
          throw Error(MemberInfoResult.message)
        } else {
          setMemberInfo(MemberInfoResult.data)
        }
      })
      .catch((err) => {
        console.log(`*** ERROR *** Failure while fetching member info for Member ID ${mbrID}: ${err}`)
      })
  }, [mbrID])

  useEffect(() => {
    if (checkPassword) {
      const password = checkPassword
      setCheckPassword(null)
      const body = {
        MBRID: mbrID,
        code: encodedPasswordValidationCode,
        qx: password
      }
      myPost('/nt/passwordChangeOperation', body)
        .then(res => {
          if (!res.ok) {
            throw Error(res.statusText)
          }
          return res.json()
        })
        .then(passwordChangeResult => {
          if (passwordChangeResult.status === 'Error') {
            setPasswordError(true)
            setPasswordHelperText(passwordChangeResult.message)
            throw Error(passwordChangeResult.message)
          } else {
            // Has to be 'Success'
            credentialSetter(passwordChangeResult.data)
            setSigninCookies(passwordChangeResult.data.idnum, passwordChangeResult.data.pword)
            setNewPasswordSuccess(true)
          }
        })
        .catch((err) => {
          console.log('*** ERROR *** Failure while validating new password: ' + err)
        })
    }
  }, [checkPassword, mbrID, encodedPasswordValidationCode, credentialSetter])

  return (
    !newPasswordSuccess
      ?
      <Stack direction='column' justifyContent='center' alignItems='center'>
        <MessageBox borderStyle='none'>
          Change password for member account {MemberInfo.pname}
        </MessageBox>
        <Stack direction='row' justifyContent='center' alignItems='flex-start' spacing={3} component='form' onSubmit={submitPasswordChange}
               sx={{height: '7rem'}}>
          <TextField id='password' name='password' label='Password' variant='outlined' fullwidth='true'
                     type={showPassword ? 'text' : 'password'}
                     InputProps={{endAdornment: <InputAdornment position='end'>
                                                  <IconButton
                                                    aria-label='toggle password visibility'
                                                    onClick={handleClickShowPassword}
                                                    onMouseDown={handleMouseDownPassword}
                                                    edge='end'
                                                  >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                  </IconButton>
                                                </InputAdornment>}}
                     error={passwordError}
                     helperText={passwordHelperText}
                     onChange={(event) => onChangeCheckPasswordField(event.currentTarget)}
                     sx={{marginTop: '0rem', width: '20rem',
                          '& .MuiOutlinedInput-root': {borderRadius: '8px', backgroundColor: 'white',
                                                       '& fieldset': {borderWidth: '2px', borderColor: 'rgba(0, 0, 0, .07)'}}}}/>
          <div style={{marginTop: '.5rem'}}>
            <Button variant='contained' type='submit' sx={{width: '13rem'}}>
              Change Password
            </Button>
          </div>
        </Stack>
      </Stack>
    : <MessageBox>
        <Stack direction='column' justifyContent='center'>
          <p>
            Password successfully updated.  {signinCredentials.pname} is signed in.
          </p>
          <MainPageButton/>
        </Stack>
      </MessageBox>
  )
}

const AvatarDragDropBox = (props) => {
  const [avatarHelperText, setAvatarHelperText] = useState('')

  /*
    The <Dropzone> approach does not capture drag/drops from webpages.  It just ignores them.
    But this approach where the below <input> tag takes the place of <DropZone> does accept them,
    but I couldn't figure out hwo to make click to bring up the directory window work, and I would
    have to figure out how to access the file information so it could be upload to the server.  I
    also couldn't figure out how to drag/drop from the Photos app, which it ignored but which
    <Dropzone> does handle.

    const myDrop = (event) => {
      event.preventDefault();
      const type = 'text'
      //const type = 'text/plain'
      //const type = 'text/html'
      //const type = 'text/uri-list'
      //const type = 'text/*'
      //const type = 'image/*'
      //const type = 'application/x-moz-file'
      const data = event.dataTransfer.getData(type)
      const Files = []
      const FileNames = []
      for (const file of event.dataTransfer.files) {
        Files.push(file)
        FileNames.push(file.name)
      }
      console.log('Data: ' + data, ', Files: ' + Files + ', File Names: ' + FileNames + ', Type: ' + type)
      event.currentTarget.value=data
    }

    ...
    <input type='text' onDrop={myDrop} style={{width: '100%', height: '20rem'}}/>
    ...
*/

  const acceptedFileTypes = {
    'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.svg']
  }

  // Attempt to upload without using useEffect
  const dropzoneOnDrop = (acceptedFiles, rejectedFiles) => {
    console.log('Accepted Files: ' + JSON.stringify(acceptedFiles) +
                ', Rejected Files: ' + JSON.stringify(rejectedFiles))
    // The objects in the acceptedFiles array are true File objects that I should be able to upload
    if (acceptedFiles.length === 0) {
      // No accepted files.  Do nothing, not even a warning message to the user, but we'll log this
      // in the console
      setAvatarHelperText('The avatar file must be an image')
      return
    }
    const formData = new FormData()
    formData.append('avatar', acceptedFiles[0])
    formData.append('mbrid', props.signinCredentials.idnum)
    formData.append('isurl', 0) // I haven't yet added the ability to drag/drop URLs.  I'm also not
                                // sure if this code on the client side is the right place to detect
                                // this, I won't know until I get drag/drop of URLs working.
    myPostForm('/nt/addAvatar', formData)
        .then(res => {
          if (!res.ok) {
            throw Error(res.statusText)
          }
          return res.json()
        })
        .then(avatarResult => {
          if (avatarResult.status === 'Error') {
            setAvatarHelperText(avatarResult.message)
            throw Error(avatarResult.message)
          } else {
            const data = avatarResult.data
            if (data.isURL) {
              setAvatarHelperText("We don't yet handle drag/drop of URL's")
            } else {
              console.log('Point A: ' + JSON.stringify(data))
              props.credentialSetter(data)
              props.avatarPopoverAnchorElSetter(null)
            }
          }
        })
        .catch((err) => {
          setAvatarHelperText('Avatar drag/drop error: ' + err)
          console.log('*** ERROR *** Failure while adding new avatar: ' + err)
        })
  }
    
  // ```` Right now I can only drag in images resident on the computer and nothing from the web.  I
  // tried onDragEnter() to see if it could in some way help, but it is not triggered when I drag in
  // an image from the web.
  return (
    <Dropzone accept={acceptedFileTypes} onDragEnter={() => console.log('Entered dropzone')}
              onDrop={dropzoneOnDrop}>
      {({getRootProps, getInputProps}) => {
        return (
        <section>
          <Stack justifyContent='center' alignItems='center' {...getRootProps()}
                 sx={{fontFamily: 'Roboto', padding: '.5rem', minHeight: '10rem', minWidth: '30rem',
                         borderRadius: '8px', borderStyle: 'solid'}}>
            <input {...getInputProps()} />
            <p>Drag/drop images here, or click to select files</p>
            <p class="Mui-error css-1wc848c-MuiFormHelperText-root">
              {avatarHelperText}
            </p>
          </Stack>
        </section>
        )}}
    </Dropzone>
  )
}

// ```` resume here - we're slowly getting there.  Tomorrow do two things:
//
// 1. Over on server side in Accounts.js there's an export.fetchMemberInfo.  Why?  Do I need it?
// Why can't I use the one in Util.js?
//
// 2. In Util.fetchMemberInfo, I should implement a class that does the updates to members so that I
// always know when the data has changed and needs to be fetched again.

const AvatarItem = (props) => {
  // const isDefaultAvatar = !(('Avatar' in props) && Object.keys(props.Avatar).length > 0)
  let isDefaultAvatar = true
  if ('Avatar' in props && props.Avatar && Object.keys(props.Avatar).length > 0) {
    isDefaultAvatar = false
  } else {
  }
  const humanReadableFilename = isDefaultAvatar
        ? null
        : props.Avatar.FileName.replace(/^\d+\|\|(.*)\|\|(\d+)\.([a-zA-Z]+)$/, '$1.$3($2)').replace(/\(0\)$/, '')
  return (
    <Grid direction='row' container justifyContent='flex-start' spacing={2}>
      <Grid item justifyContent='flex-start' alignItems='flex-end' xs={2}>
        {isDefaultAvatar
         ? <Radio size='medium' value='default' onClick={() => {props.setNewSelectedAvatar(null); props.setNewAvatarSelected(true)}}/>
         : <Radio size='medium' value={props.Avatar.FileName}
                  onClick={() => {props.setNewSelectedAvatar(props.Avatar); props.setNewAvatarSelected(true)}}/>
        }
      </Grid>
      <Grid item justifyContent='flex-start' alignItems='flex-end' xs={8}>
        {isDefaultAvatar
         ? 'Default'
         : humanReadableFilename
        }
        <br/>
        <AvatarDisplay {...props} width={50} height={60} fontSize={30}/>
      </Grid>
      <Grid item justifyContent='center' xs={2}>
        {isDefaultAvatar
         ? <Tooltip title='Add a new avatar' placement='top'>
             <IconButton onClick={(event) => props.displayAvatarPopover(event)}>
               <AddIcon/>
             </IconButton>
           </Tooltip>
         : <Tooltip title='Remove this avatar' placement='top'>
             <IconButton onClick={(event) => props.setAvatarDeletion(props.Avatar)}>
               <RemoveIcon/>
             </IconButton>
           </Tooltip>
        }
      </Grid>
    </Grid>
  )
}

const AvatarList = (props) => {
  const [avatarPopoverAnchorEl, setAvatarPopoverAnchorEl] = useState(null)
  const [avatarDeletion, setAvatarDeletion] = useState(0)
  const [newSelectedAvatar, setNewSelectedAvatar] = useState(props.signinCredentials.Avatar)
  const [newAvatarSelected, setNewAvatarSelected] = useState(false)
  const displayAvatarPopover = (event) => {
    setAvatarPopoverAnchorEl(event.currentTarget)
  }
  const closeAvatarPopover = () => {
    setAvatarPopoverAnchorEl(null)
  }
  const open = useMemo(() => {
    return Boolean(avatarPopoverAnchorEl)
  }, [avatarPopoverAnchorEl])
  const id = open ? 'avatarPopoverID' : undefined
  const Items = []

  const {signinCredentials, credentialSetter} = props
  const mbrID = signinCredentials.idnum

  useEffect(() => {
    if (avatarDeletion) {
      const body = {
        MBRID:          mbrID,
        avatarDeletion: avatarDeletion
      }
      myPost('/nt/deleteAvatar', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(deleteAvatarResult => {
          if (deleteAvatarResult.status === 'Success') {
            credentialSetter(deleteAvatarResult.data)
          } else {
            throw Error(deleteAvatarResult.message)
          }
        })
        .catch(err => {
          throw Error('*** Error *** Failure while attempting to delete avatar: ' + err)
        })
    }
  }, [avatarDeletion, mbrID, credentialSetter])

  useEffect(() => {
    if (newAvatarSelected) {
      const body = {
        MBRID:  mbrID,
        Avatar: newSelectedAvatar
      }
      setNewAvatarSelected(false)
      myPost('/nt/changeSelectedAvatar', body)
        .then(response => {
          if (!response.ok) {
            throw Error(response.statusText)
          }
          return response.json()
        })
        .then(changeSelectedAvatarResult => {
          if (changeSelectedAvatarResult.status === 'Success') {
            credentialSetter(changeSelectedAvatarResult.data)
          } else {
            throw Error(changeSelectedAvatarResult.message)
          }
        })
        .catch(err => {
          throw Error(`*** Error *** Failure while attempting to change selected avatar: ${err}`)
        })
    }
  }, [newSelectedAvatar, newAvatarSelected, mbrID, credentialSetter])

  // If no avatar is selected then the default avatar becomes the selected avatar, so we have to
  // check whether any avatar is selected.  Also need to know which radio button is selected, so
  // record the selected avatar filename.
  let selectedAvatar = 'default'
  for (const Avatar of props.signinCredentials.Avatars) {
    if (Avatar.Selected) {
      selectedAvatar = Avatar.FileName
      break
    }
  }

  // Push the default avatar first with a null Avatar object.  props.signinCredentials also has an
  // Avatar object, but adding Avatar explicitly as an argument will overwrite it
  Items.push(
    <AvatarItem key='ai_0' {...props.signinCredentials} Avatar={null} count={0}
                setNewSelectedAvatar={setNewSelectedAvatar} setNewAvatarSelected={setNewAvatarSelected}
                displayAvatarPopover={displayAvatarPopover}/>
  )

  // Now push the rest of the avatars, if any
  let count = 1
  for (const Avatar of props.signinCredentials.Avatars) {
    Items.push(
      <AvatarItem key={'ai_' + count} signinCredentials={props.signinCredentials} Avatar={Avatar} count={count++}
                  setNewSelectedAvatar={setNewSelectedAvatar} setNewAvatarSelected={setNewAvatarSelected}
                  setAvatarDeletion={setAvatarDeletion}/>
    )
  }

  return (
    <>
      <RadioGroup id='alrg' name='Avatars' value={selectedAvatar}>
        {Items}
      </RadioGroup>
      <Popover id={id} open={Boolean(avatarPopoverAnchorEl)}
               sx={{'& .css-3bmhjh-MuiPaper-root-MuiPopover-paper': {borderRadius: '8px'}}}
               anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
               transformOrigin={{vertical: 'top', horizontal: 'right'}}
               anchorEl={avatarPopoverAnchorEl} onClose={closeAvatarPopover}>
        <AvatarDragDropBox signinCredentials={props.signinCredentials}
                           credentialSetter={props.credentialSetter}
                           avatarPopoverAnchorElSetter={setAvatarPopoverAnchorEl}/>
      </Popover>
    </>
  )
}
               
const TableCell = styled(MuiTableCell)({
  fontSize: '1.2rem'
})

export const AccountSettingsEP = () => {
  const {signinCredentials, credentialSetter} = useOutletContext()
  const [retVal, setRetVal] = useState(null)
  const mbrID = useMemo(() => {
    return signinCredentials.idnum
  }, [signinCredentials.idnum])

  useEffect(() => {
    if (mbrID && mbrID > 0) {
      myFetch('/nt/fetchMemberInfo/' + mbrID)
        .then(res => res.json())
        .then(res => {
          if (res && res.status === 'Success') {
            setRetVal(res)
          }
        })
    } else {
      setRetVal({status: 'Error', message: 'You are not signed in'})
    }
  }, [mbrID])

  return (
    !retVal
      ? 'Working...'
      : (retVal.status === 'Error'
         ? <MessageBox color='red'>
             {retVal.message}
           </MessageBox>
         :
         <Stack direction='column'  className='AccountSettings'>
           <Box fontSize='2rem' sx={{textAlign: 'center'}}>
             Account Settings
           </Box>
           <TableContainer component={Paper} sx={{width: '80%', marginLeft: 'auto', marginRight: 'auto'}}>
             <Table>
               <TableBody>
                 <TableRow>
                   <TableCell sx={{width: '30%', verticalAlign: 'top'}}>
                     Aliases
                   </TableCell>
                   <TableCell>
                     <AliasList signinCredentials={signinCredentials} credentialSetter={credentialSetter}/>
                   </TableCell>
                 </TableRow>
                 <TableRow>
                   <TableCell>
                     Email Address
                   </TableCell>
                   <TableCell>
                     <EmailSetting signinCredentials={signinCredentials} credentialSetter={credentialSetter}/>
                   </TableCell>
                 </TableRow>
                 <TableRow>
                   <TableCell>
                     Password
                   </TableCell>
                   <TableCell>
                     <PasswordChangeRequest signinCredentials={signinCredentials} credentialSetter={credentialSetter}/>
                   </TableCell>
                 </TableRow>
                 <TableRow>
                   <TableCell sx={{verticalAlign: 'top'}}>
                     Avatars
                   </TableCell>
                   <TableCell>
                     <AvatarList signinCredentials={signinCredentials} credentialSetter={credentialSetter}/>
                   </TableCell>
                 </TableRow>
               </TableBody>
             </Table>
           </TableContainer>
         </Stack>
        )
  )
}
